/**
 * Clipboard API Polyfill for restricted iframe contexts
 *
 * Third-party libraries (like Thirdweb) use navigator.clipboard.writeText directly,
 * which fails in Overwolf/Discord Activities due to Permissions Policy restrictions.
 *
 * This polyfill intercepts clipboard calls and delegates to the appropriate
 * platform handler (Overwolf postMessage, GoNative bridge, or native API).
 */

const SESSION_KEY_IS_ON_OVERWOLF = "isOnOverwolf";
const OVERWOLF_MESSAGE_TYPE = "devour-overwolf";

/**
 * Detects if running in Overwolf context.
 * Uses both sessionStorage (set by React) and userAgent (available immediately)
 * to handle both early initialization and runtime checks.
 */
function isOnOverwolf(): boolean {
    return (
        window.sessionStorage.getItem(SESSION_KEY_IS_ON_OVERWOLF) === "true" ||
        window.navigator.userAgent.includes("Overwolf")
    );
}

function isGoNative(): boolean {
    return navigator.userAgent.indexOf("gonative") > -1;
}

export function initClipboardPolyfill(): void {
    if (typeof navigator === "undefined" || !navigator.clipboard) {
        return;
    }

    const originalWriteText = navigator.clipboard.writeText.bind(navigator.clipboard);

    navigator.clipboard.writeText = async (text: string): Promise<void> => {
        try {
            /*
             * Overwolf: delegate to parent window via postMessage
             * Uses same message wrapper format as sendMessageToOW in useOverwolfInterop.ts
             */
            if (isOnOverwolf()) {
                window.parent.postMessage({
                    type: OVERWOLF_MESSAGE_TYPE,
                    payload: { type: "de:copy-to-clipboard", payload: text },
                }, "*");
                return;
            }

            // GoNative: use native bridge
            if (isGoNative()) {
                // @ts-ignore - GoNative global
                if (typeof gonative !== "undefined" && gonative.clipboard?.set) {
                    // @ts-ignore
                    gonative.clipboard.set({ data: text });
                    return;
                }
            }

            // Default: use native clipboard API
            return originalWriteText(text);
        } catch (error) {
            // If all methods fail, log but don't throw to prevent UI crashes
            console.warn("Clipboard write failed:", error);
        }
    };
}
